// Smartify Message Box — the MQTT console, Super Admin only.
//
// A port of the standalone mqttbox-reactjs tool into the app, so diagnosing a
// device doesn't mean opening a separate site and re-entering a broker URL.
//
// Three panes, as in the original:
//   left    ALL MESSAGES  every device topic, newest first, searchable
//   centre  PUBLISH       a unique number + free text, preset command buttons,
//                         Add/Remove Wi-Fi, and the subscription list
//   right   FILTERED      only the subscribed unique numbers, newest first
//
// The Filtered pane is a FILTER over the same wildcard stream, not a second set
// of broker subscriptions — so adding a device to it costs nothing and shows
// history that already arrived.

import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import {
  Box, Paper, Typography, TextField, Button, IconButton, Divider, Chip,
  Select, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions,
  List, ListItem, ListItemText, CircularProgress, Tooltip,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import CloseIcon from '@mui/icons-material/Close';
import WifiIcon from '@mui/icons-material/Wifi';
import WifiOffIcon from '@mui/icons-material/WifiOff';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useNavigate } from 'react-router-dom';
import { copyText } from '../../../core/utils/clipboard';
import { useApp } from '../../../core/state/AppContext';
import {
  startMonitor, stopMonitor, subscribeMessages, clearMessages, logLocal, getMessages,
} from '../../../core/mqtt/mqttMonitor';
import { publishMessage } from '../../../core/mqtt/mqttClient';
import {
  loadSubs, onSubsChange, addSub, removeSub,
} from '../../../core/mqtt/consoleSubs';
import {
  PRESET_COMMANDS, DEBUG_COMMANDS, buildAddWifiMqtt, buildRemoveWifiMqtt,
  parseWifiList, toDeviceTopic, CMD_GET_ALL_WIFI,
} from '../../../core/devices/commands';
import {
  getLocations, getRooms, getDevicesByRoom, getDevicesByUser, getLocationWifiList, getDeviceDetail,
  getDevicesByLocation,
} from '../../../core/api/api';
import {
  primeLabels, getLabel, requestLabel, resolveLabel, subscribeLabels,
} from '../../../core/devices/deviceLabels';

const asArray = (res) => (res && Array.isArray(res.data) ? res.data : []);

// Log density is the point of this screen — you are scanning for a pattern in a
// stream, so more lines on screen beats comfortable text. These match the
// reference console: ~11px monospace on a 1.35 line-height, which fits roughly
// three times as many messages as MUI's defaults did.
const MONO = {
  fontFamily: 'ui-monospace, Menlo, Consolas, monospace',
  fontSize: 10,
  lineHeight: 1.4,
};

// One line per message, exactly as the pane renders it — time, topic, payload,
// and the device name where it is known. Plain text, so it pastes into a
// message, a ticket or a spreadsheet without carrying markup along.
function messagesAsText(messages, labelFor) {
  return messages.map((m) => {
    const label = labelFor ? labelFor(m.uno) : '';
    return `${m.time} ${m.text}${label ? ` ${label}` : ''}`;
  }).join('\n');
}

function MessageList({
  title, messages, search, onSearch, onClear, empty, labelFor,
}) {
  // 'idle' | 'ok' | 'fail' — a copy button that cannot say whether it worked is
  // worse than none, because the user only finds out at the paste.
  const [copied, setCopied] = useState('idle');

  const doCopy = async () => {
    // What is ON SCREEN, which after a search is the filtered subset — copying
    // the whole buffer would quietly ignore the filter the user just typed.
    const ok = await copyText(messagesAsText(messages, labelFor));
    setCopied(ok ? 'ok' : 'fail');
    setTimeout(() => setCopied('idle'), 1800);
  };

  return (
    <Paper variant="outlined" sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1, py: 0.5 }}>
        <Typography fontWeight={700} sx={{ flex: 1, fontSize: 13 }}>{title}</Typography>
        <Chip size="small" label={messages.length} sx={{ height: 18, fontSize: 11 }} />
        <Tooltip title={messages.length ? `Copy these ${messages.length} lines` : 'Nothing to copy'}>
          {/* span so the tooltip still works while the button is disabled */}
          <span>
            <Button
              size="small"
              onClick={doCopy}
              disabled={messages.length === 0}
              startIcon={<ContentCopyIcon sx={{ fontSize: 13 }} />}
              color={copied === 'fail' ? 'error' : copied === 'ok' ? 'success' : 'primary'}
              sx={{ minWidth: 0, fontSize: 11 }}
            >
              {copied === 'ok' ? 'Copied' : copied === 'fail' ? 'Failed' : 'Copy'}
            </Button>
          </span>
        </Tooltip>
        <Button size="small" onClick={onClear} sx={{ minWidth: 0, fontSize: 11 }}>Clear</Button>
      </Box>
      <Box sx={{ px: 1, pb: 0.5 }}>
        <TextField
          size="small"
          fullWidth
          placeholder="Search…"
          value={search}
          onChange={(e) => onSearch(e.target.value)}
          inputProps={{ style: { fontSize: 12, padding: '4px 8px' } }}
        />
      </Box>
      <Divider />
      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: 1, py: 0.25 }}>
        {messages.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ p: 1, fontSize: 12 }}>
            {empty}
          </Typography>
        ) : messages.map((m) => {
          // Which device this unique number belongs to. A raw number tells you
          // nothing while you are scanning a stream, so the breadcrumb is
          // appended where it is known.
          const label = labelFor ? labelFor(m.uno) : '';
          return (
            <Box
              key={m.id}
              sx={{
                py: '2px',
                borderBottom: 1,
                borderColor: 'divider',
                ...MONO,
                wordBreak: 'break-all',
              }}
            >
              <Box component="span" sx={{ color: 'text.secondary', mr: 0.75 }}>{m.time}</Box>
              {m.text}
              {label && (
                // nowrap so the name never breaks mid-word ("4 Touch Swit / ch"):
                // the row sets break-all for the payload, which must not apply here.
                <Box
                  component="span"
                  sx={{ color: 'success.light', ml: 0.75, fontWeight: 700, whiteSpace: 'nowrap' }}
                >
                  {label}
                </Box>
              )}
            </Box>
          );
        })}
      </Box>
    </Paper>
  );
}

export default function MqttConsoleScreen() {
  const navigate = useNavigate();
  const { userId, isSuperAdmin } = useApp();

  const [messages, setMessages] = useState([]);
  const [allSearch, setAllSearch] = useState('');
  const [filteredSearch, setFilteredSearch] = useState('');
  const [subs, setSubs] = useState([]);

  const [topic, setTopic] = useState('');
  const [text, setText] = useState('');
  const [subInput, setSubInput] = useState('');

  // Loc / Room / Device pickers
  const [locations, setLocations] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [devices, setDevices] = useState([]);
  const [locId, setLocId] = useState('');
  const [roomId, setRoomId] = useState('');
  const [devUno, setDevUno] = useState('');

  const [allDevices, setAllDevices] = useState([]);

  const [wifiOpen, setWifiOpen] = useState(false);
  const [ssid, setSsid] = useState('');
  const [wifiPass, setWifiPass] = useState('');
  const [removeOpen, setRemoveOpen] = useState(false);
  const [wifiList, setWifiList] = useState(null);
  const [savedWifi, setSavedWifi] = useState(null);
  const [wifiLocationId, setWifiLocationId] = useState(undefined);
  // The location's registered-network query failed (see getLocationWifiList) — so
  // "no networks" must not be claimed.
  const [wifiDbFailed, setWifiDbFailed] = useState(false);
  const wifiWaitRef = useRef(false);

  // ── monitor lifecycle ────────────────────────────────────────────────────
  useEffect(() => {
    startMonitor();
    const off = subscribeMessages((buf) => setMessages([...buf]));
    setSubs(loadSubs());
    const offSubs = onSubsChange((s) => setSubs([...s]));
    return () => { off(); offSubs(); stopMonitor(); };
  }, []);

  // Device list, for the breadcrumb and the pickers.
  useEffect(() => {
    if (!userId) return;
    getLocations(userId).then((r) => setLocations(asArray(r))).catch(() => {});
    getDevicesByUser(userId).then((r) => setAllDevices(asArray(r))).catch(() => {});
  }, [userId]);

  useEffect(() => {
    if (!locId) { setRooms([]); setRoomId(''); return; }
    getRooms(userId, locId).then((r) => setRooms(asArray(r))).catch(() => {});
  }, [userId, locId]);

  useEffect(() => {
    if (!roomId) { setDevices([]); setDevUno(''); return; }
    getDevicesByRoom(userId, roomId, locId).then((r) => setDevices(asArray(r))).catch(() => {});
  }, [userId, roomId, locId]);

  // ── publishing ───────────────────────────────────────────────────────────
  // Raw, with no [uno]…#userId envelope — this is a diagnostic console and its
  // whole point is to send EXACTLY what you typed. The reference tool does the
  // same; the firmware accepts both forms.
  const send = useCallback((message) => {
    const t = topic.trim();
    if (!t || !message) return;
    const full = toDeviceTopic(t);
    publishMessage(full, message);
    logLocal(`➡️ published "${message}" to ${full}`);
  }, [topic]);

  // The same, with the `[<uno>]` prefix the ESP32 firmware needs in order to
  // treat a command as addressed rather than as a broadcast every device on the
  // topic acts on (OTA_CLAUDE.md §5.1). Used for the Wi-Fi verbs, which are the
  // ones where a stray broadcast would rewrite a whole site's credentials.
  //
  // Still no `#<userId>` — the ESP8266 does not strip it for those two verbs and
  // it ends up inside the password. See core/devices/commands.js.
  //
  // A topic typed with a '/' is left alone: the operator has addressed something
  // by hand and this must not second-guess it.
  const sendAddressed = useCallback((message) => {
    const t = topic.trim();
    if (!t || !message) return;
    send(t.includes('/') ? message : `[${t}]${message}`);
  }, [topic, send]);

  // uno -> "Location/Room/Device".
  //
  // ⚠️ NOT just the signed-in user's devices. This console watches '#', so most
  // of what scrolls past belongs to OTHER users — and an earlier version, which
  // built this map from getDevicesByUser alone, called every one of them
  // "Unknown device" even though GetDeviceDetailbyUniqueNumber names them
  // perfectly well. The numbers you least recognise are the ones you most need
  // named, so unknown ones are now looked up (see core/devices/deviceLabels).
  useEffect(() => { primeLabels(allDevices); }, [allDevices]);

  // Repaint when a background lookup lands. The labels themselves live in the
  // resolver's cache, not in state.
  const [labelTick, setLabelTick] = useState(0);
  useEffect(() => subscribeLabels(() => setLabelTick((n) => n + 1)), []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const labelFor = useCallback((uno) => getLabel(uno) || '', [labelTick]);

  // Name every number that appears in the log. Cheap: the resolver ignores
  // numbers it already knows or has already queued.
  useEffect(() => {
    messages.forEach((m) => { if (m.uno && m.uno !== 'app') requestLabel(m.uno); });
  }, [messages]);

  // The device the unique number refers to, so you can see what you're about to
  // command before you command it. Resolved directly rather than through the
  // queue — this is the one number the user is actually looking at.
  const [breadcrumb, setBreadcrumb] = useState('');
  useEffect(() => {
    const uno = topic.trim();
    if (!uno) { setBreadcrumb(''); return undefined; }
    // null means never looked up; '' is a real answer ("no such device") and
    // must resolve synchronously, or every repaint would flash "…" again.
    const known = getLabel(uno);
    if (known !== null) { setBreadcrumb(known || 'Unknown device'); return undefined; }
    let alive = true;
    setBreadcrumb('…');
    resolveLabel(uno).then((l) => { if (alive) setBreadcrumb(l || 'Unknown device'); });
    return () => { alive = false; };
  }, [topic, labelTick]);

  // The device's LocationID.
  //
  // ⚠️ NOT available from getDevicesByUser: that list carries LocationName but
  // NO LocationID, so an earlier version always resolved null and told the user
  // "this is not one of your devices" while simultaneously showing its
  // Location/Room/Name breadcrumb from the very same list.
  //
  // Two ways to get it, in order:
  //   1. GetDeviceDetailbyUniqueNumber — authoritative, and works for ANY
  //      unique number, including one not mapped to this user.
  //   2. match on name — getLocations returns "<user>-<LocationName>"
  //      (e.g. "Sanjay Ailani-Factory") while the device list has the raw name
  //      ("Factory"), so the comparison is on the suffix.
  const resolveLocationId = useCallback(async (uno) => {
    const detail = asArray(await getDeviceDetail(uno).catch(() => null))[0];
    const fromDetail = detail && (detail.LocationID ?? detail.LocationId);
    if (fromDetail != null) return fromDetail;

    const d = allDevices.find((x) => String(x.UniqueNumber) === String(uno));
    const name = String(d?.LocationName || '').trim().toLowerCase();
    if (!name) return null;
    const loc = locations.find((l) => {
      const full = String(l.LocationName || '').trim().toLowerCase();
      return full === name || full.endsWith(`-${name}`);
    });
    return loc ? loc.LocationID : null;
  }, [allDevices, locations]);

  const filtered = useMemo(() => {
    const unos = new Set(subs.map((s) => s.uniqueNumber));
    return messages.filter((m) => unos.has(m.uno));
  }, [messages, subs]);

  const matches = (list, q) => {
    const s = q.trim().toLowerCase();
    return s ? list.filter((m) => m.text.toLowerCase().includes(s)) : list;
  };

  // ── wifi ─────────────────────────────────────────────────────────────────
  const doAddWifi = () => {
    if (!topic.trim() || !ssid) return;
    sendAddressed(buildAddWifiMqtt(ssid, wifiPass));
    setSsid(''); setWifiPass(''); setWifiOpen(false);
  };

  // Every Wi-Fi network known for this device's location, so another one can be
  // pushed to this device without retyping it.
  //
  // Two sources, merged and de-duplicated by SSID:
  //   • the location's registered networks (GetAllWifiNetworksByLocationId)
  //   • whatever OTHER DEVICES in this location have reported to `getallwifi`
  //     during this session — those replies are already in the monitor buffer,
  //     so this costs nothing and surfaces networks that were pushed straight to
  //     a device and never registered against the location.
  //
  // ⚠️ GetAllWifiNetworksByLocationId IS CURRENTLY BROKEN ON THE SERVER: it is a
  // real route (not a 404) but never sends a response, for any LocationID —
  // verified against ids that do and don't exist. Flutter calls the same endpoint
  // and hangs the same way. Until that is fixed server-side, the registered list
  // comes back empty after the timeout and only the device-reported source fills
  // the picker, so the dialog says which sources actually answered rather than
  // implying the location has no networks.
  const openAddWifi = async () => {
    setWifiOpen(true);
    setSavedWifi(null);
    setWifiLocationId(undefined);
    setWifiDbFailed(false);

    const uno = topic.trim();
    const locationId = await resolveLocationId(uno);
    setWifiLocationId(locationId);
    if (locationId == null) { setSavedWifi([]); return; }

    const dbRows = await getLocationWifiList(locationId).then(asArray).catch(() => null);
    if (dbRows === null) setWifiDbFailed(true);
    const registered = (dbRows || [])
      .map((w) => ({ ssid: w.WIFISSID ?? '', pass: w.WIFIPASSWORD ?? '', from: 'location' }))
      .filter((w) => w.ssid);

    // Unique numbers that sit in the same location.
    //
    // ⚠️ NOT from the signed-in user's device list. This console works on ANY
    // unique number, and for someone else's device that list holds nothing — so
    // the sibling set came out empty and no device-reported network was ever
    // offered. GetAllDevicesByLocationID answers for any location.
    const siblingRes = await getDevicesByLocation(locationId).catch(() => null);
    const siblings = new Set(asArray(siblingRes).map((d) => String(d.UniqueNumber)));

    const seen = [];
    getMessages().forEach((m) => {
      if (!siblings.has(m.uno) || !m.topic.endsWith('/client')) return;
      parseWifiList(m.payload.replace(/^\[[^\]]*\]/, '')).forEach((w) => {
        seen.push({ ...w, from: 'device' });
      });
    });

    const bySsid = new Map();
    [...registered, ...seen].forEach((w) => {
      if (w.ssid && !bySsid.has(w.ssid)) bySsid.set(w.ssid, w);
    });
    setSavedWifi([...bySsid.values()]);
  };

  const openRemoveWifi = () => {
    if (!topic.trim()) return;
    setWifiList(null);
    setRemoveOpen(true);
    wifiWaitRef.current = true;
    send(CMD_GET_ALL_WIFI);
  };

  // The device answers getallwifi on its /client topic; pick the reply out of
  // the live stream rather than opening a second listener.
  useEffect(() => {
    if (!wifiWaitRef.current || !removeOpen) return;
    const uno = topic.trim();
    const reply = messages.find((m) => m.uno === uno && m.topic.endsWith('/client')
      && m.payload.includes(','));
    if (reply) {
      const list = parseWifiList(reply.payload.replace(/^\[[^\]]*\]/, ''));
      if (list.length) { setWifiList(list); wifiWaitRef.current = false; }
    }
  }, [messages, removeOpen, topic]);

  if (!isSuperAdmin) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography color="text.secondary">This page is for Super Admin only.</Typography>
      </Box>
    );
  }

  // Compact command buttons — these are one-tap actions in a dense tool, so they
  // are sized to fit many per row rather than to be prominent individually.
  const btn = {
    textTransform: 'none',
    minWidth: 0,
    fontSize: 11,
    py: 0.25,
    px: 1,
    lineHeight: 1.5,
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      {/* No title bar. The app header above already says where you are, and this
          screen is three scrolling logs — every row spent on chrome is a row of
          messages you cannot see. Back lives inline next to Publish; Escape
          works globally. */}
      <Box sx={{
        flex: 1, minHeight: 0, display: 'grid', gap: 0.75, p: 0.75,
        gridTemplateColumns: { xs: '1fr', md: '1fr 1.05fr 1fr' },
      }}
      >
        {/* ── ALL ─────────────────────────────────────────────────────── */}
        <MessageList
          title="All Messages"
          messages={matches(messages, allSearch)}
          search={allSearch}
          onSearch={setAllSearch}
          onClear={clearMessages}
          labelFor={labelFor}
          empty="Listening… messages appear here as devices report."
        />

        {/* ── PUBLISH ─────────────────────────────────────────────────── */}
        <Paper variant="outlined" sx={{ p: 0.75, overflowY: 'auto', minHeight: 0 }}>
          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
            <Tooltip title="Back (Esc)">
              <IconButton size="small" onClick={() => navigate('/home')} sx={{ p: 0.5 }}>
                <ArrowBackIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {/* Wide enough for a full unique number — it was being clipped to
                "93580!", which is exactly the field you need to read. */}
            <TextField
              size="small" placeholder="Unique number" value={topic}
              onChange={(e) => setTopic(e.target.value)}
              sx={{ width: 118, flexShrink: 0 }}
              inputProps={{ style: { ...MONO, fontSize: 12, padding: '5px 6px' } }}
            />
            <TextField
              size="small" placeholder="Msg" value={text} fullWidth
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { send(text); setText(''); } }}
              inputProps={{ style: { fontSize: 12, padding: '5px 6px' } }}
            />
            <Button
              variant="contained" size="small"
              onClick={() => { send(text); setText(''); }}
              disabled={!topic.trim() || !text.trim()}
              sx={{ ...btn, px: 1, minWidth: 36 }}
            >
              <SendIcon fontSize="small" />
            </Button>
          </Box>

          <Typography sx={{ display: 'block', fontSize: 11, color: 'text.secondary', minHeight: 15 }}>
            {breadcrumb}
          </Typography>

          {/* Every command in ONE wrapping row. They were on three rows with
              full-width buttons, which pushed the subscription controls off the
              visible area for no gain — these are one-tap actions, not choices
              that need emphasis. */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
            {PRESET_COMMANDS.map((c) => (
              <Button key={c} size="small" variant="contained" sx={btn}
                disabled={!topic.trim()} onClick={() => send(c)}
              >
                {c}
              </Button>
            ))}
            {DEBUG_COMMANDS.map((c) => (
              <Button key={c} size="small" variant="contained" color="success" sx={btn}
                disabled={!topic.trim()} onClick={() => send(c)}
              >
                {c}
              </Button>
            ))}
            <Button size="small" variant="contained" sx={btn}
              startIcon={<WifiIcon sx={{ fontSize: 14 }} />}
              disabled={!topic.trim()} onClick={openAddWifi}
            >
              add wifi
            </Button>
            <Button size="small" variant="contained" color="error" sx={btn}
              startIcon={<WifiOffIcon sx={{ fontSize: 14 }} />}
              disabled={!topic.trim()} onClick={openRemoveWifi}
            >
              remove wifi
            </Button>
          </Box>

          <Divider sx={{ my: 0.75 }} />

          {/* Add to the Filtered pane */}
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <TextField
              size="small" fullWidth placeholder="Unique number to watch"
              value={subInput} onChange={(e) => setSubInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && subInput.trim()) { addSub(subInput.trim()); setSubInput(''); }
              }}
              inputProps={{ style: { fontSize: 12, padding: '5px 6px' } }}
            />
            <Button size="small" variant="outlined" sx={btn} disabled={!subInput.trim()}
              onClick={() => { addSub(subInput.trim()); setSubInput(''); }}
            >
              Sub
            </Button>
          </Box>

          <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
            <Select size="small" fullWidth displayEmpty value={locId}
              onChange={(e) => setLocId(e.target.value)}
            >
              <MenuItem value=""><em>Loc</em></MenuItem>
              {locations.map((l) => (
                <MenuItem key={l.LocationID} value={l.LocationID}>{l.LocationName}</MenuItem>
              ))}
            </Select>
            <Select size="small" fullWidth displayEmpty value={roomId}
              onChange={(e) => setRoomId(e.target.value)} disabled={!locId}
            >
              <MenuItem value=""><em>Room</em></MenuItem>
              {rooms.map((r) => (
                <MenuItem key={r.RoomID} value={r.RoomID}>{r.RoomName}</MenuItem>
              ))}
            </Select>
            <Select size="small" fullWidth displayEmpty value={devUno}
              onChange={(e) => setDevUno(e.target.value)} disabled={!roomId}
            >
              <MenuItem value=""><em>Device</em></MenuItem>
              {devices.map((d) => (
                <MenuItem key={d.UniqueNumber} value={d.UniqueNumber}>
                  {d.DeviceName || d.UniqueNumber}
                </MenuItem>
              ))}
            </Select>
            <Button size="small" variant="outlined" sx={btn} disabled={!devUno}
              onClick={() => {
                const d = devices.find((x) => String(x.UniqueNumber) === String(devUno));
                addSub(devUno, d?.DeviceName || devUno);
              }}
            >
              Sub
            </Button>
          </Box>

          <Typography variant="subtitle2" sx={{ mt: 0.75, fontSize: 12 }}>
            {`Subscribed (${subs.length})`}
          </Typography>
          <List dense>
            {subs.map((s) => (
              <ListItem
                key={s.uniqueNumber}
                secondaryAction={(
                  <Tooltip title="Stop watching">
                    <IconButton edge="end" size="small" color="error"
                      onClick={() => removeSub(s.uniqueNumber)}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              >
                <ListItemText
                  primary={s.label === s.uniqueNumber ? s.uniqueNumber : `${s.label} · ${s.uniqueNumber}`}
                  primaryTypographyProps={{ sx: MONO }}
                />
              </ListItem>
            ))}
            {subs.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ px: 2 }}>
                Nothing watched yet — add a unique number above.
              </Typography>
            )}
          </List>
        </Paper>

        {/* ── FILTERED ────────────────────────────────────────────────── */}
        <MessageList
          title="Filtered"
          messages={matches(filtered, filteredSearch)}
          search={filteredSearch}
          onSearch={setFilteredSearch}
          onClear={clearMessages}
          labelFor={labelFor}
          empty="Add a unique number to watch it here."
        />
      </Box>

      {/* Add Wi-Fi — exact literal payload, see commands.js */}
      <Dialog open={wifiOpen} onClose={() => setWifiOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          Add Wi-Fi to {topic}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            {breadcrumb}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 1 }}>
          {/* Networks already registered against this device's location. Picking
              one avoids retyping — and mistyping — a password that is already
              known to work at that site. */}
          <Typography variant="subtitle2">Saved for this location</Typography>
          {savedWifi === null ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1 }}>
              <CircularProgress size={16} />
              <Typography variant="body2">Loading…</Typography>
            </Box>
          ) : savedWifi.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {wifiLocationId == null
                ? "Couldn't work out which location this device belongs to."
                /* Never claim "none" when the query that would know never
                   answered — see getLocationWifiList. */
                : wifiDbFailed
                  ? "The server's list of saved networks for this location did not "
                    + 'respond, so only networks seen on a device are shown. Run '
                    + 'getallwifi on a device here to discover more.'
                  : 'No Wi-Fi known for this location yet. Run getallwifi on a device '
                    + 'here to discover what it already has.'}
            </Typography>
          ) : (
            <List dense sx={{ maxHeight: 180, overflowY: 'auto', bgcolor: 'action.hover', borderRadius: 1 }}>
              {savedWifi.map((w, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <ListItem key={`${w.ssid}-${i}`} disablePadding>
                  <Button
                    fullWidth
                    sx={{ justifyContent: 'flex-start', textTransform: 'none', px: 2 }}
                    onClick={() => { setSsid(w.ssid); setWifiPass(w.pass); }}
                  >
                    <ListItemText
                      primary={w.ssid}
                      secondary={w.pass}
                      primaryTypographyProps={{ sx: { fontSize: 13, fontWeight: 600 } }}
                      secondaryTypographyProps={{ sx: MONO }}
                    />
                    <Chip
                      size="small"
                      label={w.from === 'device' ? 'on a device' : 'location'}
                      sx={{ height: 16, fontSize: 9 }}
                    />
                  </Button>
                </ListItem>
              ))}
            </List>
          )}

          {/* The list is partial, and saying so is the difference between "these
              are the networks here" and "these are the ones we could reach". */}
          {wifiDbFailed && savedWifi && savedWifi.length > 0 && (
            <Typography variant="caption" color="warning.main">
              Server list unavailable — showing only networks seen on a device.
            </Typography>
          )}

          <Divider>or enter a new one</Divider>

          <TextField size="small" label="SSID" value={ssid} onChange={(e) => setSsid(e.target.value)} />
          <TextField size="small" label="Password" value={wifiPass} onChange={(e) => setWifiPass(e.target.value)} />
          <Typography variant="caption" color="text.secondary">
            Sent verbatim — SSID and password are case-sensitive.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWifiOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={doAddWifi} disabled={!ssid}>
            Send to device
          </Button>
        </DialogActions>
      </Dialog>

      {/* Remove Wi-Fi — asks the device for its list, then removes one */}
      <Dialog open={removeOpen} onClose={() => setRemoveOpen(false)}>
        <DialogTitle>Wi-Fi on {topic}</DialogTitle>
        <DialogContent sx={{ minWidth: 320 }}>
          {wifiList === null ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 2 }}>
              <CircularProgress size={20} />
              <Typography variant="body2">Asking the device…</Typography>
            </Box>
          ) : wifiList.length === 0 ? (
            <Typography variant="body2">No Wi-Fi networks found on the device.</Typography>
          ) : (
            <List dense>
              {wifiList.map((w, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <ListItem key={`${w.ssid}-${i}`} secondaryAction={(
                  <IconButton edge="end" size="small" color="error"
                    onClick={() => { sendAddressed(buildRemoveWifiMqtt(w.ssid, w.pass)); setRemoveOpen(false); }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                )}
                >
                  <ListItemText primary={w.ssid} primaryTypographyProps={{ sx: MONO }} />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemoveOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
